import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus, Upload, Shield, X, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { adminAPI } from '../../utils/api';
import { toast } from 'sonner';
import AdminNav from '../../components/admin/AdminNav';

const REASONS = ['Influencer', 'Tester', 'Friend', 'Support', 'Other'];
const STATUSES = ['active', 'pending', 'expired', 'revoked'];

const statusBadge = (status) => {
  const map = {
    'Paid Pro': 'bg-emerald-500/15 text-emerald-500',
    'Free Pro Access': 'bg-accent/15 text-accent',
    'Free': 'bg-muted text-muted-foreground',
    active: 'bg-emerald-500/15 text-emerald-500',
    pending: 'bg-amber-500/15 text-amber-500',
    expired: 'bg-muted text-muted-foreground',
    revoked: 'bg-red-500/15 text-red-500',
  };
  return map[status] || 'bg-muted text-muted-foreground';
};

const UsersAccess = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);

  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [emails, setEmails] = useState('');
  const [reason, setReason] = useState('Influencer');
  const [expiresAt, setExpiresAt] = useState('');
  const [granting, setGranting] = useState(false);

  const [statusFilter, setStatusFilter] = useState('active');
  const [grants, setGrants] = useState([]);
  const [loadingGrants, setLoadingGrants] = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    adminAPI.checkAdmin()
      .then((res) => {
        if (!res.data?.is_admin) { navigate('/home'); return; }
        setIsAdmin(true);
      })
      .catch(() => navigate('/home'))
      .finally(() => setChecking(false));
  }, [navigate]);

  const fetchGrants = useCallback(() => {
    setLoadingGrants(true);
    setSelected([]);
    adminAPI.listGrants(statusFilter)
      .then((res) => setGrants(res.data?.grants || []))
      .catch(() => toast.error('Failed to load grants'))
      .finally(() => setLoadingGrants(false));
  }, [statusFilter]);

  useEffect(() => { if (isAdmin) fetchGrants(); }, [isAdmin, fetchGrants]);

  const doSearch = () => {
    if (query.trim().length < 2) { toast.info('Enter at least 2 characters'); return; }
    setSearching(true);
    adminAPI.searchUsers(query.trim())
      .then((res) => setSearchResults(res.data?.results || []))
      .catch(() => toast.error('Search failed'))
      .finally(() => setSearching(false));
  };

  const summarize = (s) => {
    const parts = [];
    if (s.created?.length) parts.push(`${s.created.length} activated`);
    if (s.pending?.length) parts.push(`${s.pending.length} pending`);
    if (s.duplicates?.length) parts.push(`${s.duplicates.length} already granted`);
    if (s.invalid?.length) parts.push(`${s.invalid.length} invalid`);
    return parts.join(', ') || 'No changes';
  };

  const doGrant = () => {
    if (!emails.trim()) { toast.info('Enter at least one email'); return; }
    setGranting(true);
    adminAPI.createGrants(emails, reason, expiresAt)
      .then((res) => {
        toast.success('Grant applied: ' + summarize(res.data));
        setEmails('');
        fetchGrants();
      })
      .catch(() => toast.error('Grant failed'))
      .finally(() => setGranting(false));
  };

  const doCsv = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('reason', reason);
    if (expiresAt) fd.append('expires_at', expiresAt);
    setGranting(true);
    adminAPI.uploadGrantsCsv(fd)
      .then((res) => { toast.success('CSV imported: ' + summarize(res.data)); fetchGrants(); })
      .catch(() => toast.error('CSV import failed'))
      .finally(() => { setGranting(false); e.target.value = ''; });
  };

  const revoke = (grantId, email) => {
    adminAPI.revokeGrants([grantId], null)
      .then(() => { toast.success(`Revoked ${email}`); fetchGrants(); })
      .catch(() => toast.error('Revoke failed'));
  };

  const revocableIds = grants.filter((g) => g.status === 'active' || g.status === 'pending').map((g) => g.grant_id);
  const allSelected = revocableIds.length > 0 && selected.length === revocableIds.length;
  const toggleOne = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.concat([id])));
  const toggleAll = () => setSelected(allSelected ? [] : revocableIds);
  const bulkRevoke = () => {
    if (selected.length === 0) return;
    adminAPI.revokeGrants(selected, null)
      .then((res) => { toast.success(`Revoked ${res.data?.revoked ?? selected.length} grant(s)`); fetchGrants(); })
      .catch(() => toast.error('Bulk revoke failed'));
  };

  const grantFromSearch = (email) => {
    adminAPI.createGrants(email, 'Support', null)
      .then((res) => { toast.success('Grant applied: ' + summarize(res.data)); doSearch(); fetchGrants(); })
      .catch(() => toast.error('Grant failed'));
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="animate-spin text-accent" /></div>;
  }
  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background pb-16" data-testid="admin-users-access-page">
      <div className="max-w-4xl mx-auto px-4 pt-8">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="text-accent" size={22} />
          <h1 className="font-heading font-bold text-2xl">Users &amp; Access</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Grant complimentary Fitxess Pro. Stored separately from real purchases — revoking always falls back to a user's real subscription.
        </p>
        <AdminNav />

        {/* Grant Free Pro */}
        <section className="bg-card border border-border/40 rounded-2xl p-5 mb-6" data-testid="grant-section">
          <h2 className="font-heading font-semibold text-lg mb-3 flex items-center gap-2">
            <UserPlus size={18} className="text-accent" /> Grant Free Pro
          </h2>
          <textarea
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder="Enter one or many emails, separated by commas or new lines"
            rows={3}
            data-testid="grant-emails-input"
            className="w-full rounded-xl border border-border/60 bg-background p-3 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
          <div className="flex flex-wrap items-end gap-3 mt-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Reason</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="grant-reason-select"
                className="h-10 rounded-xl border border-border/60 bg-background px-3 text-sm"
              >
                {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Expires (optional — blank = lifetime)</label>
              <Input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value ? new Date(e.target.value).toISOString() : '')}
                data-testid="grant-expiry-input"
                className="h-10 w-44"
              />
            </div>
            <Button onClick={doGrant} disabled={granting} data-testid="grant-submit-btn"
              className="h-10 rounded-full bg-accent text-white font-semibold hover:bg-accent/90">
              {granting ? <Loader2 size={16} className="animate-spin mr-1" /> : <UserPlus size={16} className="mr-1" />}
              Grant Free Pro
            </Button>
            <label className="h-10 inline-flex items-center gap-2 px-4 rounded-full bg-secondary border border-border/40 text-sm font-semibold cursor-pointer hover:bg-secondary/80" data-testid="grant-csv-label">
              <Upload size={16} /> Upload CSV
              <input type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={doCsv} data-testid="grant-csv-input" />
            </label>
          </div>
        </section>

        {/* Search users */}
        <section className="bg-card border border-border/40 rounded-2xl p-5 mb-6" data-testid="search-section">
          <h2 className="font-heading font-semibold text-lg mb-3 flex items-center gap-2">
            <Search size={18} className="text-accent" /> Find a user
          </h2>
          <div className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doSearch()}
              placeholder="Search by email, name, or exact user ID"
              data-testid="user-search-input"
              className="h-10"
            />
            <Button onClick={doSearch} disabled={searching} data-testid="user-search-btn" className="h-10 rounded-full">
              {searching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            </Button>
          </div>
          {searchResults.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="search-results">
              {searchResults.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border/40" data-testid="search-result-row">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{u.name || '—'} · <span className="text-muted-foreground">{u.email}</span></p>
                    <p className="text-xs text-muted-foreground">ID {u.masked_user_id} · Source: {u.access_source}{u.provider ? ` · ${u.provider}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={'px-2 py-1 rounded-full text-xs font-semibold ' + statusBadge(u.status)}>{u.status}</span>
                    {u.status !== 'Paid Pro' && u.status !== 'Free Pro Access' && (
                      <Button onClick={() => grantFromSearch(u.email)} data-testid="search-grant-btn"
                        className="h-8 rounded-full bg-accent text-white text-xs px-3">Grant Pro</Button>
                    )}
                    {u.status === 'Free Pro Access' && u.grant?.grant_id && (
                      <Button onClick={() => revoke(u.grant.grant_id, u.email)} variant="outline"
                        data-testid="search-revoke-btn" className="h-8 rounded-full text-xs px-3">Revoke</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Grants list */}
        <section className="bg-card border border-border/40 rounded-2xl p-5" data-testid="grants-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-heading font-semibold text-lg">Grants</h2>
            <div className="flex gap-1">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  data-testid={`grants-filter-${s}`}
                  className={'px-3 h-8 rounded-full text-xs font-semibold capitalize transition-colors ' +
                    (statusFilter === s ? 'bg-accent text-white' : 'bg-secondary text-foreground hover:bg-secondary/80')}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          {/* Bulk actions bar (only where rows are revocable) */}
          {revocableIds.length > 0 && (
            <div className="flex items-center justify-between gap-3 mb-3 p-2 rounded-xl bg-secondary/50 border border-border/40" data-testid="bulk-actions-bar">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={allSelected} onChange={toggleAll}
                  data-testid="grants-select-all" className="w-4 h-4 accent-accent" />
                Select all ({revocableIds.length})
              </label>
              <Button onClick={bulkRevoke} disabled={selected.length === 0}
                variant="outline" data-testid="bulk-revoke-btn"
                className="h-8 rounded-full text-xs px-3 disabled:opacity-40">
                <X size={14} className="mr-1" /> Revoke selected ({selected.length})
              </Button>
            </div>
          )}
          {loadingGrants ? (
            <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-accent" /></div>
          ) : grants.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="grants-empty">No {statusFilter} grants</p>
          ) : (
            <div className="space-y-2" data-testid="grants-list">
              {grants.map((g) => {
                const revocable = g.status === 'active' || g.status === 'pending';
                return (
                <div key={g.grant_id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border/40" data-testid="grant-row">
                  <div className="flex items-center gap-3 min-w-0">
                    {revocable && (
                      <input type="checkbox" checked={selected.includes(g.grant_id)}
                        onChange={() => toggleOne(g.grant_id)}
                        data-testid={`grant-checkbox-${g.grant_id}`}
                        className="w-4 h-4 accent-accent shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{g.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {g.reason || 'No reason'} · {g.expires_at ? `expires ${new Date(g.expires_at).toLocaleDateString()}` : 'lifetime'} · by {g.granted_by}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={'px-2 py-1 rounded-full text-xs font-semibold capitalize ' + statusBadge(g.status)}>{g.status}</span>
                    {revocable && (
                      <Button onClick={() => revoke(g.grant_id, g.email)} variant="outline"
                        data-testid="grant-revoke-btn" className="h-8 rounded-full text-xs px-3">
                        <X size={14} className="mr-1" /> Revoke
                      </Button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default UsersAccess;
